import ReactDOMServer from 'react-dom/server';
import BillTable from '../BillTable';
import BankDetails from './BankDetails';
import CustomizedInputText from './CustomizedInputText';
import { isEmpty } from 'lodash';
import { Box } from '@mui/system';

/**
 * Generates the HTML content for an email body based on the provided data.
 * Renders a styled HTML template including a bill table, customized input text, and bank details.
 *
 * @param {Object} params - The parameters for generating the email body.
 * @param {Array} params.rows - The rows of the bill table.
 * @param {number} params.subTotal - The subtotal amount.
 * @param {boolean} params.prepago - Indicates if it's a prepago.
 * @param {boolean} params.isAdmin - Indicates if the user is an admin.
 * @param {boolean} params.isEmailPreview - Indicates if it's an email preview.
 * @param {Object} params.bankAccount - The bank account details.
 * @param {Object} params.personalizedText - The personalized text to be included.
 * @param {Object} params.combinedBillOrg - The combined organization bill.
 * @param {Object} params.combinedBill - The combined bill details.
 * @returns {string} The HTML content for the email body.
 */
export const generateEmailBody = ({
  rows,
  subTotal,
  prepago,
  isAdmin,
  isEmailPreview,
  bankAccount,
  personalizedText,
  combinedBillOrg,
  combinedBill,
}) => {
  return ReactDOMServer.renderToStaticMarkup(
    <div>
      <style>
        {`
      .tableContainer {
        background-color: #f5f5f5;
        border: 2px solid rgba(128, 128, 128, 0.4);
        overflow-y: auto;
      }

      .tableContainer::-webkit-scrollbar {
        width: 5px; /* Ancho de la barra de desplazamiento vertical */
      }

      .tableContainer::-webkit-scrollbar-thumb {
        background-color: #888; /* Color de la barra de desplazamiento vertical */
      }

      .tableContainer::-webkit-scrollbar-thumb:hover {
        background-color: #555; /* Color de la barra de desplazamiento vertical hover */
      }

      .tableRow {
        border-top: 2px solid black;
      }

      .spacedCell {
        padding-left: 45px;
      }

      table {
        border-collapse: collapse;
        width: 100%;
      }

      th, td {
        border-bottom: 1px solid black;
        text-align: left;
        padding: 8px;
      }
      th:last-child {
        text-align: right;
      }
      tr:has(th:nth-of-type(1):only-child) th {
        text-align: center;
      }
      tr td:last-child {
        text-align: right;
      }
      th {
        border-bottom: 2px solid black;
      }

      tr {
        background-color: #f5f5f5;
      }

      .rowPrepagoNext {
        background-color: #90EE90;
      }

      .rowSubTotal {
        background-color: #FFFFD0;
      }

      .rowPrepagoPrevious {
        background-color: #FFCCCC;
      }

      .rowTotal {
        background-color: #BBDEFB;
      }
    `}
      </style>
      <BillTable
        rows={rows}
        subTotal={subTotal}
        prepago={prepago}
        isAdmin={isAdmin}
        isEmailPreview={isEmailPreview}
      />
      {!isEmpty(combinedBillOrg) && !isEmpty(combinedBill) && (
        <>
          <Box
            sx={{
              paddingTop: 2,
              paddingBottom: 2,
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <h2 style={{ fontSize: '.9rem', margin: '10px 0', textAlign: 'center' }}>
              Facturación de {combinedBillOrg.displayName}
            </h2>
          </Box>
          <BillTable
            rows={combinedBill.rows}
            subTotal={combinedBill.subTotal}
            prepago={combinedBill.prepago}
            isAdmin={isAdmin}
            isEmailPreview={isEmailPreview}
          />
        </>
      )}
      <CustomizedInputText personalizedText={personalizedText} />
      <BankDetails bankAccount={bankAccount} />
    </div>,
  );
};

/**
 * Generates an HTML email template for sending invoices to clients.
 *
 * @param {string} content - The content of the invoice to be included in the email.
 * @param {string} month - The month for which the invoice is generated.
 * @returns {string} The HTML content of the email template with the invoice details.
 */
export const generateEmailTemplate = (content, month) => {
  return `
  <!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Factura Prestobots</title>
</head>
<body style="font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 0; color: #333;">
  <div class="container" style="width: 100%; max-width: 1200px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); overflow: hidden;">
      <div class="header" style="background-color: #ffcc01; padding: 20px; text-align: center;">
          <h1 style="margin: 0; font-size: 1.5rem;">Factura Prestobots ${month}</h1>
      </div>
      <div class="content" style="padding: 20px;">
          <h2 style="font-size: 1.25; margin: 0 0 10px;">Estimado Cliente,</h2>
          <p style="margin: 0 0 15px; line-height: 1.6;">A continuación, encontrará los detalles de su facturación:</p>
          
          ${content}

          <p style="margin-top: 20px;">Si tiene alguna pregunta sobre su factura, no dude en <a href="mailto:administracion@prestobots.com" style="color: #4CAF50; text-decoration: none;">contactarnos</a>.</p>
          <p style="line-height: 1.6;">Atentamente,<br>El equipo de Prestobots</p>
      </div>
      <div class="footer" style="background-color: #f4f4f4; color: #777777; padding: 20px; text-align: center; font-size: 0.875rem;">
          <p style="margin: 0;">&copy; 2024 Prestobots. Todos los derechos reservados.</p>
      </div>
  </div>
</body>
</html>
  `;
};
